import React, {useContext, useEffect, useRef, useState} from "react";
import BaseInstallment from "./BaseInstallment";
import {initialAllowedCalculationParametersForCredit} from "./initialAllowedParametersForCredit";
import {goKredytService} from "../../../utils/axios";
import ContractLengthSlider from "./ContractLengthSlider";
import FeeSlider from "./FeeSlider";
import {CustomerTypeContext, isCustomerCompany} from "../../../context/customer-type-context";
import MathUtils from "../../../utils/MathUtils";
import InstallmentSubmitBox from "./InstallmentSubmitBox";
import analytics from "../../../utils/analytics";
import * as axios from "axios";
import ContactDialog from "../../../components/ContactDialog/ContactDialog";
import CreditCalculationSummary from "./CreditCalculationSummary";
import VehicleInsuranceCheckbox from "./VehicleInsuranceCheckbox";
import VehicleGapCheckbox from "./VehicleGapCheckbox";

function CreditCalculator(props) {

    const customerContext = useContext(CustomerTypeContext);
    const {forwardRef, useImperativeHandle} = React;
    const feeSliderRef = useRef();
    const [stepSize, setStepSize] = useState(500);

    useEffect(() => {
        setStepSize(feeSliderRef.current.getStepSize())
    }, []);

    let contractLengthRef = useRef(null);
    let initialFeeRef = useRef(null);
    const source = axios.CancelToken.source();

    let vehiclePrice = isCustomerCompany(customerContext) ? props.offer.minimumPriceGross : props.offer.minimumPriceGross;

    const [calculationResponse, setCalculationResponse] = useState({
        installment: props.offer.installmentGross,
        rrso: 0,
        loanCost: 0
    });

    const [isGwarancjaZwrotuActive, setIsGwarancjaZwrotuActive] = useState(props.offer.hasReturnGuarantee);

    let defaultContractLength = props.initialCalculation ? props.initialCalculation.contractLength : 120;

    const [calculationParameters, setCalculationParameters] = useState(
        {
            financingAmount: props.offer.minimumPriceGross,
            contractLength: defaultContractLength,
            initialFeeValue: props.initialCalculation ? MathUtils.percentageOfValue(vehiclePrice, props.initialCalculation.initialFeeValueNet) : 10,
            productionYear: props.offer.manufactureYear
        }
    );

    useEffect(() => {
        goKredytService.post(props.calculatorUrl, {...calculationParameters, ...(props.extraCalculationParams || {})}, {
            headers: {'Content-Type': 'application/json'},
            cancelToken: source.token
        }).then(response => {
            let calculationResult = response.data;
            if (calculationResult !== undefined) {
                setCalculationResponse(calculationResult);
            }
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.log("request cancelled: " + error.message);
            } else {
                console.log("another error happened: " + error.message);
            }
        });

        let currentState = (calculationParameters.insuranceLength !== 0)
            && calculationParameters.includeGapInsurance;

        setIsGwarancjaZwrotuActive(props.offer.hasReturnGuarantee && currentState);

        return () => {
            source.cancel();
        };
    }, [calculationParameters, props.offer.hasReturnGuarantee, isGwarancjaZwrotuActive]);

    const [allowedCalculationParameters, setAllowedCalculationParameters] = useState(initialAllowedCalculationParametersForCredit);

    useEffect(() => {
        goKredytService(props.allowedCalculationParametersUrl).then(response => {
            let allowedParameters = response.data;
            if (allowedParameters !== undefined) {
                setAllowedCalculationParameters(allowedParameters);
                contractLengthChangeHandler(allowedParameters.contractLengthMonths.at(-1));
            }
        });
    }, [props.offer.minimumPriceGross]);

    const contractLengthChangeHandler = (newContractLength) => {
        setCalculationParameters(prevState => {
            if (prevState.contractLength !== newContractLength) {
                analytics.sendEvent({
                    category: 'OL - zmiana miesiecy',
                    action: 'Klikniecie',
                    label: 'Kliknieto w ' + newContractLength,
                    value: 1
                });
            }
            return {...prevState, contractLength: newContractLength};
        });
    };

    const initialFeeChangeHandler = (initialFee) => {
        let initialFeePercentage = MathUtils.percentageOfValue(vehiclePrice, initialFee);
        setCalculationParameters(prevState => ({...prevState, initialFeeValue: initialFeePercentage}));
    };

    const goToContractLengthHandler = () => {
        analytics.sendEvent({category: 'OL', action: 'Klikniecie', label: 'Zmieniono OL', value: 1});
        window.scrollTo({
            behavior: 'smooth',
            top: contractLengthRef.current.getBoundingClientRect().top + window.scrollY - 50
        });
    };

    const goToInitialFeeHandler = () => {
        analytics.sendEvent({category: 'OW', action: 'Klikniecie', label: 'Zmieniono OW', value: 1});
        window.scrollTo({
            behavior: 'smooth',
            top: initialFeeRef.current.getBoundingClientRect().top + window.scrollY - 50
        });
    }

    const [insuranceChecked, setInsuranceChecked] = useState(false);
    const [insuranceData, setInsuranceData] = useState(null);

    const [gapChecked, setGapChecked] = useState(false);
    const [gapData, setGapData] = useState(null);
    const gapFinancingType = props.gapFinancingType || "CREDIT";

    const [isOpenContactDialog, setIsOpenContactDialog] = useState(false);

    const reservationClickHandler = () => {
        analytics.sendEvent({category: 'Zarezerwuj', action: 'Klikniecie', label: 'Kliknieto w Zarezerwuj', value: 1});
        const initialFeeGross = MathUtils.valueOfPercentage(vehiclePrice, calculationParameters.initialFeeValue);
        const initialFeeNet = isCustomerCompany(customerContext)
            ? initialFeeGross
            : Math.round(initialFeeGross / 1.23);
        const installmentNet = isCustomerCompany(customerContext)
            ? calculationResponse.installment
            : Math.round(calculationResponse.installment / 1.23);
        let calculationOffer = {
            vehicle: props.offer,
            calculation: {
                vehicleValueNet: props.offer.minimumPriceNet,
                vehicleValueGross: props.offer.minimumPriceGross,
                initialFeeValueNet: initialFeeNet,
                initialFeeValueGross: initialFeeGross,
                installmentNet: installmentNet,
                installmentGross: calculationResponse.installment,
                contractLength: calculationParameters.contractLength,
                ocAcInsuranceChecked: insuranceChecked,
                ocAcInsuranceData: insuranceChecked ? insuranceData : null,
                gapChecked: gapChecked,
                gapData: gapChecked ? gapData : null
            }
        };
        props.reservationStrategy(true, calculationOffer);
    }

    const contactClickHandler = () => {
        setIsOpenContactDialog(true);
        analytics.sendEvent({category: 'Zapytaj', action: 'Klikniecie', label: 'Kliknieto w Zapytaj', value: 1});
    }

    return (
        <div className="calc__wrapper">
            <div className="calc__content">
                <BaseInstallment
                    installment={{
                        net: calculationResponse.installment,
                        gross: calculationResponse.installment
                    }}
                    offer={props.offer}
                    label="Rata kredytu"
                    isCreditCalculation={true}
                />
                <form action="">
                    <span ref={contractLengthRef}/>
                    <ContractLengthSlider
                        items={allowedCalculationParameters.contractLengthMonths}
                        value={calculationParameters.contractLength}
                        onChange={contractLengthChangeHandler}
                        label={props.periodLabel || "Okres kredytu (ilość miesięcznych rat)"}
                    />
                    <div ref={initialFeeRef} className="calc__form-item">
                        <FeeSlider
                            ref={feeSliderRef}
                            infoCode="oplata-wstepna"
                            onChange={initialFeeChangeHandler}
                            defaultValue={MathUtils.valueOfPercentage(vehiclePrice, calculationParameters.initialFeeValue)}
                            value={MathUtils.valueOfPercentage(vehiclePrice, calculationParameters.initialFeeValue)}
                            brutto={!isCustomerCompany(customerContext)}
                            isCreditCalculation={true}
                            vehiclePrice={vehiclePrice}
                            min={isCustomerCompany(customerContext) ?
                                allowedCalculationParameters.initialFeeValues.minimumInitialFeeGross : allowedCalculationParameters.initialFeeValues.minimumInitialFeeGross}
                            max={isCustomerCompany(customerContext) ?
                                allowedCalculationParameters.initialFeeValues.maximumInitialFeeGross - stepSize : allowedCalculationParameters.initialFeeValues.maximumInitialFeeGross - stepSize}
                        />
                    </div>
                    <VehicleInsuranceCheckbox
                        vehicleId={props.offer.id}
                        checked={insuranceChecked}
                        onChange={() => setInsuranceChecked(prev => !prev)}
                        onDataLoaded={setInsuranceData}
                    />
                    <VehicleGapCheckbox
                        vehicleId={props.offer.id}
                        financingType={gapFinancingType}
                        financingMonths={calculationParameters.contractLength}
                        checked={gapChecked}
                        onChange={() => setGapChecked(prev => !prev)}
                        onDataLoaded={setGapData}
                        showFallback={true}
                    />
                    <p className="calc__insurance-disclaimer">*Składka ma charakter poglądowy i może ulec zmianie.</p>
                </form>
            </div>
            <div className="calc__summary">
                <CreditCalculationSummary
                    isNew={props.offer.isNew}
                    calculationParameters={calculationParameters}
                    calculationResult={calculationResponse}
                    changeContractLength={goToContractLengthHandler}
                    changeInitialFee={goToInitialFeeHandler}
                    initialFeeValue={MathUtils.valueOfPercentage(vehiclePrice, calculationParameters.initialFeeValue)}
                    hideRrso={props.hideRrso}
                    productNoun={props.productNoun}
                    insuranceChecked={insuranceChecked}
                    insuranceData={insuranceData}
                    gapChecked={gapChecked}
                    gapData={gapData}
                    gapFinancingType={gapFinancingType}
                />
                <InstallmentSubmitBox
                    installment={{
                        net: calculationResponse.installment,
                        gross: calculationResponse.installment
                    }}
                    onReservationClick={reservationClickHandler}
                    onSendMessageClick={contactClickHandler}
                    isCreditCalculation={true}
                />
            </div>
            <ContactDialog isOpen={isOpenContactDialog}
                           onClose={() => setIsOpenContactDialog(false)}
                           additionalMessage={"Dotyczy oferty: " + window.location.href}
            />
        </div>
    );
}

export default CreditCalculator;
